import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import FormField from "./form-field";
import { useSearch } from "./search-context";
import { advancedFieldConfig } from "./field-config";
import FormFieldDate from "./form-field-date";
import FormFieldSelect from "./form-field-select";
import AddressLookup from "../address-lookup/address-lookup";
import useValidateFormData from "./validation/validator";

interface AdvancedSearchFormProps {
  resetErrors: () => void;
}

const AdvancedSearchForm = ({ resetErrors }: AdvancedSearchFormProps) => {
  const form = useSearch();
  const update = form.updateFormData;
  const validateForm = useValidateFormData();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    update({ [name]: value });
    validateForm();

    if (value.trim()) {
      resetErrors();
    }
  };

  const handleFieldChange = (name: string, value: string | null) => {
    update({ [name]: value });
    validateForm();
  };

  return (
    <form>
      <Grid container spacing={3} rowSpacing={3}>
        {advancedFieldConfig.map((group) => (
          <Box
            data-testid="advanced-search-panel"
            key={`group-${group[0].name}`}
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : theme.palette.background.paper,
              padding: (theme) => theme.spacing(3),
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              width: "100%",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                  : "0 2px 4px rgba(0, 0, 0, 0.08)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                    : "0 4px 12px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <Grid container spacing={2} rowSpacing={2}>
              {group.map((field) => {
                if (field.divider) {
                  return (
                    <Grid
                      key={`divider-${field.name}`}
                      size={{ xs: field.size }}
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "text.primary",
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          mb: 2,
                        }}
                      >
                        {field?.dividerText}
                      </Typography>
                    </Grid>
                  );
                }

                if (field?.type === "date") {
                  return (
                    <Grid key={`date-${field.name}`} size={{ xs: field.size }}>
                      <FormFieldDate
                        label={`${field?.label}${field?.optional ? " (Optional)" : ""}`}
                        value={
                          form[field?.name as keyof typeof form]?.toString() ??
                          ""
                        }
                        onChange={(value) =>
                          handleFieldChange(field?.name, value)
                        }
                        required={field.required}
                      />
                    </Grid>
                  );
                }

                if (field?.type === "address") {
                  return (
                    <Grid key={`date-${field.name}`} size={{ xs: field.size }}>
                      <AddressLookup handleChange={handleFieldChange} />
                    </Grid>
                  );
                }

                if (field?.type === "select") {
                  return (
                    <Grid
                      key={`select-${field.name}`}
                      size={{ xs: field.size }}
                    >
                      <FormFieldSelect
                        labelValue={field?.label}
                        defaultValue={
                          form[field?.name as keyof typeof form]?.toString() ??
                          ""
                        }
                        selectValues={field?.selectValues ?? []}
                        onSelectChange={(value) =>
                          handleFieldChange(field?.name, value)
                        }
                      />
                    </Grid>
                  );
                }

                return (
                  <Grid key={`input-${field.name}`} size={{ xs: field.size }}>
                    <FormField
                      label={`${field?.label}${field?.optional ? " (Optional)" : ""}`}
                      name={field?.name}
                      value={
                        form[field?.name as keyof typeof form]?.toString() ?? ""
                      }
                      onChange={handleChange}
                      required={field.required}
                      endIcon={field.icon}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Grid>
    </form>
  );
};

export default AdvancedSearchForm;
